// src/pages/BookingPage.jsx
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import CustomBookingForm from '../components/CustomBookingForm';
// 必要に応じてFramer Motion等のアニメーションライブラリを追加しても良いです

const BookingPage = () => {
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const isSuccess = urlParams.get("status") === "success";
    const isEnglish = location.pathname.startsWith('/en');
    const canonicalUrl = `https://kanazawa-stay-akebono.com${location.pathname}`;

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <Helmet>
                <title>
                    {isSuccess
                        ? (isEnglish ? 'Booking Completed | Kanazawa Stay Akebono' : 'ご予約完了 | 金沢之旅宿 あけぼの')
                        : (isEnglish ? 'Book Now | Kanazawa Stay Akebono' : 'ご予約 | 金沢之旅宿 あけぼの')}
                </title>
                <link rel="canonical" href={canonicalUrl} />
            </Helmet>

            {/* ▼ 1. ヒーローセクション（画像を背景に） ▼ */}
            {!isSuccess && (
                <div style={{
                    width: '100%',
                    height: '40vh',
                    minHeight: '300px',
                    backgroundImage: 'url(/images/machiya2.jpg)', // 宿の魅力的な画像を指定
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '80px' // ヘッダーの高さに合わせて調整
                }}>
                    <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.4)'
                    }}></div>
                    <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', color: '#fff' }}>
                        <h1 style={{ fontSize: '2.5rem', fontFamily: '"Noto Serif JP", serif', marginBottom: '10px', letterSpacing: '0.1em' }}>
                            {isEnglish ? 'Reserve Your Stay' : 'ご宿泊予約'}
                        </h1>
                        <p style={{ fontSize: '1.1rem', letterSpacing: '0.05em' }}>
                            {isEnglish ? 'Best Rate Guaranteed on Official Website' : '公式サイトからのご予約が最もお得です。'}
                        </p>
                    </div>
                </div>
            )}

            <div style={{ paddingTop: isSuccess ? '150px' : '60px', paddingBottom: '100px', backgroundColor: '#fcfaf7', minHeight: '100vh', width: '100%' }}>
                <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 20px' }}>

                    {/* 予約フォーム本体 */}
                    <div className="mt-4">
                        <CustomBookingForm isEnglish={isEnglish} />
                    </div>

                    {!isSuccess && (
                        <>
                            {/* ▼ 2. 文化体験へのティーザー（クロスセル導線） ▼ */}
                            <div style={{
                                marginTop: '60px',
                                padding: '40px 30px',
                                backgroundColor: '#fff',
                                borderRadius: '4px',
                                boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                                textAlign: 'center'
                            }}>
                                <h3 style={{ fontSize: '1.5rem', color: '#080752', fontFamily: '"Noto Serif JP", serif', marginBottom: '15px' }}>
                                    {isEnglish ? 'Exclusive Cultural Experiences' : 'ご宿泊者様限定 特別文化体験'}
                                </h3>
                                <p style={{ color: '#666', lineHeight: '1.8', marginBottom: '25px', fontSize: '0.95rem' }}>
                                    {isEnglish
                                        ? 'We offer private cultural experiences exclusively for our guests. You can easily request arrangements for Tea Ceremonies, Zen Calligraphy, Kaga Yuzen, and more after completing your room reservation.'
                                        : '当サイトからご予約いただいたお客様限定で、プライベートなお茶会や加賀友禅、書道などの特別体験を手配いたします。ご宿泊予約の完了後、専用ページよりお気軽にお申し込みいただけます。'}
                                </p>
                                <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginBottom: '25px', flexWrap: 'wrap' }}>
                                    {/* 実際の画像パスに合わせてください */}
                                    <img src="/images/tea-1.jpg" alt="茶道" style={{ width: '30%', minWidth: '150px', borderRadius: '4px', objectFit: 'cover', height: '120px' }} />
                                    <img src="/images/maida-studio.jpg" alt="加賀友禅" style={{ width: '30%', minWidth: '150px', borderRadius: '4px', objectFit: 'cover', height: '120px' }} />
                                    <img src="/images/shodo-1.jpg" alt="書道" style={{ width: '30%', minWidth: '150px', borderRadius: '4px', objectFit: 'cover', height: '120px' }} />
                                </div>
                                <a
                                    href={isEnglish ? '/en/special-offer' : '/special-offer'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#B39D82', textDecoration: 'none', borderBottom: '1px solid #B39D82', fontWeight: 'bold' }}
                                >
                                    {isEnglish ? 'Explore Experiences ↗' : '体験プログラムの詳細を見る ↗'}
                                </a>
                            </div>

                            {/* ▼ 3. ポリシー・ご案内セクション（視認性向上） ▼ */}
                            <div style={{
                                marginTop: '40px',
                                padding: '40px 30px',
                                backgroundColor: '#fdfbf7',
                                borderTop: '2px solid #e0d8c0',
                                borderBottom: '2px solid #e0d8c0',
                            }}>
                                <h3 style={{ fontSize: '1.3rem', color: '#080752', marginBottom: '30px', fontFamily: '"Noto Serif JP", serif', textAlign: 'center' }}>
                                    {isEnglish ? 'Policies & Information' : 'ご宿泊に関するご案内'}
                                </h3>

                                <div style={{ maxWidth: '800px', margin: '0 auto', fontSize: '0.95rem', color: '#444', lineHeight: '1.8' }}>
                                    <div style={{ marginBottom: '20px', paddingLeft: '15px', borderLeft: '3px solid #B39D82' }}>
                                        <strong style={{ color: '#080752' }}>{isEnglish ? 'Cancellation Policy' : 'キャンセルポリシー'}</strong><br />
                                        {isEnglish
                                            ? 'Free cancellation until 15 days before check-in. Cancellations made within 14 days of arrival and no-shows will incur a 100% cancellation fee.'
                                            : '到着日の14日前の前日までは無料でキャンセル（全額返金）可能です。到着日の14日前以降のキャンセル、および無断不泊の場合は、宿泊料金の100%をキャンセル料として申し受けます。'}
                                    </div>
                                    <div style={{ marginBottom: '20px', paddingLeft: '15px', borderLeft: '3px solid #B39D82' }}>
                                        <strong style={{ color: '#080752' }}>{isEnglish ? 'Child Policy' : 'お子様のご宿泊について'}</strong><br />
                                        {isEnglish
                                            ? 'Children under 5 years old stay free of charge (no extra bedding). Children aged 6 and older will be charged the adult rate.'
                                            : '5歳未満で寝具をご利用にならない（添い寝）場合は無料でご宿泊いただけます。6歳以上のお客様は大人料金となります。'}
                                    </div>
                                    <div style={{ marginBottom: '20px', paddingLeft: '15px', borderLeft: '3px solid #B39D82' }}>
                                        <strong style={{ color: '#080752' }}>{isEnglish ? 'Meals' : 'お食事について'}</strong><br />
                                        {isEnglish
                                            ? 'Room only. No meals are provided.'
                                            : '当館は素泊まりの宿です。お食事の提供はございません。出張シェフの手配は別途承ります。'}
                                    </div>
                                    <div style={{ marginBottom: '30px', paddingLeft: '15px', borderLeft: '3px solid #B39D82' }}>
                                        <strong style={{ color: '#080752' }}>{isEnglish ? 'Changes to Booking' : 'ご予約の変更について'}</strong><br />
                                        {isEnglish
                                            ? 'To change your travel dates or the number of guests, please cancel your current reservation and make a new booking.'
                                            : 'ご宿泊日程や人数の変更をご希望の場合は、お手数ですが一度現在のご予約をキャンセルの上、再度新規でのご予約をお願いいたします。'}
                                    </div>

                                    <div style={{ textAlign: 'center', marginTop: '40px' }}>
                                        <a
                                            href={isEnglish ? '/en/lookup' : '/lookup'}
                                            style={{
                                                display: 'inline-block',
                                                padding: '12px 30px',
                                                backgroundColor: 'transparent',
                                                color: '#080752',
                                                border: '1px solid #080752',
                                                textDecoration: 'none',
                                                borderRadius: '4px',
                                                fontWeight: 'bold',
                                                fontSize: '0.95rem',
                                                transition: 'all 0.3s ease'
                                            }}
                                            onMouseOver={(e) => { e.target.style.backgroundColor = '#080752'; e.target.style.color = '#fff'; }}
                                            onMouseOut={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = '#080752'; }}
                                        >
                                            {isEnglish ? 'Check / Cancel Booking →' : 'ご予約の確認・キャンセルはこちら →'}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default BookingPage;